import { AxiosRequestConfig } from "axios";
import { Spinner } from "components";
import NavbarWithActions from "components/Navbar/navbarwithactions";
import { CalendarDays, ChevronDown } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  GetEarningForL2,
  GetEarningL2ReqType,
} from "service/apis/Opportunities";
import { toast } from "sonner";

// Constants
const FINANCIAL_YEARS = [
  { label: "FY 2023 – 2024", start: "2023-04-01", end: "2024-03-31" },
  { label: "FY 2024 – 2025", start: "2024-04-01", end: "2025-03-31" },
  { label: "FY 2025 – 2026", start: "2025-04-01", end: "2026-03-31" },
];

// Date formatter (DD-MM-YY)
const formatDateDDMMYY = (input: string | number) => {
  const date = new Date(input);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = String(date.getFullYear());
  return `${dd}-${mm}-${yyyy}`;
};

// Summary Card Config (separated array)
const getSummaryCardConfig = (summary: any) => [
  {
    label: "Total no. of Transactions",
    value: summary.number_transactions,
    bg: "#FFF6E7",
    imagePath: "/images/Transactions.svg",
  },
  {
    label: "Total value of Transactions",
    value: `₹${summary.total_transactions.toLocaleString()}`,
    bg: "#F0FDFF",
    imagePath: "/images/money.svg",
  },
  {
    label: "Total Earning",
    value: `₹${summary.total_earnings.toLocaleString()}`,
    bg: "#F5F5FF",
    imagePath: "/images/total_earning.svg",
  },
  {
    label: "Balance Payout",
    value: `₹${summary.balance.toLocaleString()}`,
    bg: "#F2FFDF",
    imagePath: "/images/balance.svg",
  },
];

const formatDateTime = (input: string | number) => {
  const date = new Date(input);

  const day = String(date.getDate()).padStart(2, "0");
  const monthShort = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  const hours24 = date.getHours();
  const hours12 = hours24 % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours24 >= 12 ? "pm" : "am";

  return `${day}-${monthShort}-${year} | ${hours12}:${minutes} ${ampm}`;
};

const MyEarningPage = () => {
  const [loading, setLoading] = useState(false);
  const [orderList, setOrderList] = useState<any[]>([]);
  const [summary, setSummary] = useState({
    number_transactions: 0,
    total_transactions: 0,
    total_earnings: 0,
    balance: 0,
  });
  const [selectedFY, setSelectedFY] = useState(FINANCIAL_YEARS[2]);

  const fetchAllEarningList = async (startDate: string, endDate: string) => {
    setLoading(true);
    try {
      const authToken = JSON.parse(localStorage.getItem("authToken") || "");
      const payload: AxiosRequestConfig<GetEarningL2ReqType> = {
        headers: { Authorization: `Bearer ${authToken}` },
        params: {
          start_date: formatDateDDMMYY(startDate),
          end_date: formatDateDDMMYY(endDate),
        },
      };
      const res: any = await GetEarningForL2(payload);
      if (res.status === 200) {
        const data = res.data || {};
        setOrderList(data?.order_list || []);
        setSummary({
          number_transactions: data?.number_transactions || 0,
          total_transactions: data?.total_transactions || 0,
          total_earnings: data?.total_earnings || 0,
          balance: data?.balance || 0,
        });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Earning API error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllEarningList(selectedFY.start, selectedFY.end);
  }, [selectedFY]);

  return (
    <div className="space-y-3">
      {loading && <Spinner />}
      <NavbarWithActions header='My earnings'/>
      <div className="p-6 sm:p-2 bg-white min-h-screen flex flex-col gap-3 rounded-[12px] bg-[#fff] opacity-100 rotate-0">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-sm font-semibold text-[#999999]">
            Payment Updates
          </h2>
          <div className="relative inline-flex items-center gap-2 px-5 py-2 border border-blue-100 rounded-md text-blue-600 text-sm bg-white cursor-pointer">
            <select
              className="appearance-none bg-transparent pr-12 text-sm font-medium text-blue-600 
               cursor-pointer border-none focus:outline-none focus:ring-0 focus:border-none"
              value={selectedFY.label}
              onChange={(e) =>
                setSelectedFY(
                  FINANCIAL_YEARS.find((fy) => fy.label === e.target.value) ||
                    FINANCIAL_YEARS[0]
                )
              }
            >
              {FINANCIAL_YEARS.map((fy) => (
                <option key={fy.label} value={fy.label} className="text-black">
                  {fy.label}
                </option>
              ))}
            </select>

            {/* Dropdown Arrow */}
            <ChevronDown
              size={16}
              className="text-gray-500 absolute right-8 pointer-events-none"
            />

            {/* Calendar Icon */}
            <CalendarDays
              size={16}
              className="text-blue-600 absolute right-2 pointer-events-none"
            />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {getSummaryCardConfig(summary).map((card, index) => (
            <div
              key={index}
              className="p-4 rounded-xl"
              style={{ backgroundColor: card.bg }}
            >
              <div className="flex flex-col gap-[18px]">
                <h3 className="text-2xl font-bold text-[#333333]">
                  {card.value}
                </h3>
                <div className="flex items-center gap-2">
                  <img
                    src={card.imagePath}
                    alt={card.label}
                    className="w-5 h-5"
                  />
                  <p className="text-base font-semibold text-[#666666]">
                    {card.label}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Payout Table */}
        <div>
          <h2 className="text-sm font-semibold text-[#999999] mb-3">Payouts</h2>
          <div className="overflow-x-auto border rounded-lg">
            <table className="w-full text-sm text-left">
              <thead className="bg-[#165FE3] text-[#fff]">
                <tr>
                  <th className="px-4 py-3 sm:px-2 sm:py-1 font-medium">
                    Order ID
                  </th>
                  <th className="px-4 py-3 sm:px-2 sm:py-1 font-medium">
                    Customer
                  </th>
                  <th className="px-4 py-3 sm:px-2 sm:py-1 font-medium">
                    FCY Amount
                  </th>
                  <th className="px-4 py-3 sm:px-2 sm:py-1 font-medium">
                    Earning
                  </th>
                  <th className="px-4 py-3 sm:px-2 sm:py-1 font-medium">
                    Payout Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {orderList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center px-4 py-5 sm:px-2 sm:py-2 text-gray-500"
                    >
                      No earnings data found for selected financial year.
                    </td>
                  </tr>
                ) : (
                  orderList.map((tx, index) => (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                    >
                      <td className="px-4 py-3 sm:px-2 sm:py-2">
                        <p className="font-medium">ID-{tx?.id || "--"}</p>
                        <p className="text-xs text-gray-500">
                          {tx?.created_at
                            ? formatDateTime(tx.created_at)
                            : "--"}
                        </p>
                      </td>
                      <td className="px-4 py-3 sm:px-2 sm:py-2">
                        {tx?._customers?.full_name || "--"}
                      </td>
                      <td className="px-4 py-3 sm:px-2 sm:py-2">
                        {tx?.currency} {tx?.amount?.toLocaleString() || 0}
                      </td>
                      <td className="px-4 py-3 sm:px-2 sm:py-2">
                        INR {tx?.l2_profit?.toLocaleString() || 0}
                      </td>
                      <td className="px-4 py-3 sm:px-2 sm:py-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            tx?.mark_paid
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200 text-[#666666]"
                          }`}
                        >
                          {tx?.mark_paid ? "Received" : "Pending"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyEarningPage;
